import React, { useEffect, useMemo } from 'react';
import { Bell, ExternalLink } from 'lucide-react';
import { useZunelo } from '../../context/ZuneloContext';
import {
  notificationDestinationLabel,
  sortNotificationsNewestFirst,
} from '../../domain/notifications';
import { formatRelativeTime } from '../../domain/types';
import { BrandIcon } from '../../ui/common';
import './notifications.css';

export const NotificationsView: React.FC = () => {
  const {
    notifications,
    notificationsLoading,
    notificationsError,
    markNotificationsSeen,
    refreshNotifications,
  } = useZunelo();
  const sorted = useMemo(() => sortNotificationsNewestFirst(notifications), [notifications]);
  const latestTimestamp = sorted[0]?.updatedAt;

  useEffect(() => {
    markNotificationsSeen();
  }, [latestTimestamp, markNotificationsSeen]);

  if (notificationsLoading && sorted.length === 0) {
    return <div className="z-notifications-state">Cargando actividad reciente…</div>;
  }
  if (notificationsError && sorted.length === 0) {
    return (
      <div className="z-notifications-state" role="alert">
        <span>{notificationsError}</span>
        <button type="button" onClick={() => void refreshNotifications()}>
          Reintentar
        </button>
      </div>
    );
  }
  if (sorted.length === 0) {
    return (
      <div className="z-notifications-empty">
        <Bell size={24} aria-hidden="true" />
        <h2>Aquí aparecerá lo que requiera tu atención</h2>
        <p>Cada aviso incluirá el motivo y un acceso directo al ítem.</p>
      </div>
    );
  }

  return (
    <div className="z-notifications-ledger" aria-label="Actividad de notificaciones">
      <div className="z-notifications-ledger-heading">
        <span>Actividad reciente</span>
        <span>{sorted.length} avisos</span>
      </div>
      {sorted.map((notification) => {
        const href = notification.appPath ?? notification.externalUrl;
        const content = (
          <>
            <span className="z-notification-provider" aria-hidden="true">
              {notification.source === 'system' ? (
                <Bell size={18} />
              ) : (
                <BrandIcon source={notification.source} size={18} />
              )}
            </span>
            <span className="z-notification-copy">
              <strong>{notification.title}</strong>
              {notification.body ? <span>{notification.body}</span> : null}
              <span className="z-notification-destination">
                {notificationDestinationLabel(notification.source)}
                <ExternalLink size={13} aria-hidden="true" />
              </span>
            </span>
            <time dateTime={notification.updatedAt}>
              {formatRelativeTime(notification.updatedAt)}
            </time>
          </>
        );
        return href ? (
          <a
            className="z-notification-row"
            href={href}
            key={notification.id}
            {...(!notification.appPath ? { target: '_blank', rel: 'noreferrer' } : {})}
          >
            {content}
          </a>
        ) : (
          <div className="z-notification-row" key={notification.id}>
            {content}
          </div>
        );
      })}
    </div>
  );
};
